import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import {
  TIntegrationRuleForm,
  integrationRuleSchema,
} from "./integrationRuleSchema";
import {
  FormContainer,
  FormTabs,
  Treturn,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import {
  createIntegrationRule,
  getIntegrationRuleById,
  updateIntegrationRuleById,
} from "../../../../../services/integrationRule.service";
import { useNavigate, useParams } from "react-router-dom";
import { IntegrationRuleRelationshipGroupPage } from "./IntegrationRuleRelationshipGroup/IntegrationRuleRelationshipGroupPage";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MiscellaneousServicesRoundedIcon from "@mui/icons-material/MiscellaneousServicesRounded";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsIcon from "@mui/icons-material/Notifications";
import RuleIcon from "@mui/icons-material/Rule";
import { IntegrationRuleIServicePage } from "./IntegrationRuleIService/IntegrationRuleIServicesPage";
import { BasicData } from "./BasicData";
import { dateOnlyStringToNewDate } from "../../../../../utils/conversions";
import { IntegrationRuleMovPage } from "./IntegrationRuleMov/IntegrationRuleMovPage";
import { IntegrationRuleParamPage } from "./IntegrationRuleParam/IntegrationRuleParamPage";
import { IntegrationRuleNotificationsPage } from "./IntegrationRuleNotification/IntegrationRuleNotificationsPage";
import { IntegrationRuleFieldPage } from "./IntegrationRuleField/IntegrationRuleFieldPage";

const translationPath = "page.4uBus.integrationRule.";

const SYNC_PHYSICAL_PERSON_SINGLE_BASE_UPDATE_AUTOMATION_CODES = [
  14, 15, 16, 17,
];
const physicalPersonSingleBaseAutomationCodes = [14, 15, 16, 17];

const TASY_OPS_EXECUTION_REQUEST_AUTOMATION_CODE = 19;

const getValidBatchSize = (value?: number | null) =>
  Number.isInteger(value) && Number(value) >= 1 ? Number(value) : 50;

const getValidMaxWorkers = (value?: number | null) =>
  Number.isInteger(value) && Number(value) >= 1 && Number(value) <= 5
    ? Number(value)
    : 5;

const IntegrationRule = () => {
  const navigate = useNavigate();

  const { eI, id } = useParams();

  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createIntegrationRule,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateIntegrationRuleById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getIntegrationRuleById,
  );

  const { setMessage } = useNotificationContext();

  const [busAutomationCode, setBusAutomationCode] = useState<number | null>(
    null,
  );
  const [busAutomationId, setBusAutomationId] = useState<string | null>(null);

  const withAutomationOptions = (v: TIntegrationRuleForm) => {
    const { onlyRequestableLabExams, ...rest } = v;
    return v.uBusAutomation?.code === TASY_OPS_EXECUTION_REQUEST_AUTOMATION_CODE
      ? { ...rest, onlyRequestableLabExams: onlyRequestableLabExams ?? false }
      : rest;
  };

  const saveHandler = (v: TIntegrationRuleForm) => {
    return save({
      ...withAutomationOptions(v),
      _4uBusAutomationId: v.uBusAutomation?.id,
      iProcesseId: v.iProcesse?.id,
      startDate: v.startDate?.toISOString().substring(0, 10),
      endDate: v.endDate?.toISOString().substring(0, 10),
      initialDate: v.initialDate?.toISOString().substring(0, 10),
    });
  };

  const updateHandler = (v: TIntegrationRuleForm, id: string) =>
    update({
      item: {
        ...withAutomationOptions(v),
        _4uBusAutomationId: v.uBusAutomation?.id,
        iProcesseId: v.iProcesse?.id,
        startDate: v.startDate?.toISOString().substring(0, 10),
        endDate: v.endDate?.toISOString().substring(0, 10),
        initialDate: v.initialDate?.toISOString().substring(0, 10),
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TIntegrationRuleForm>> => {
      const { data, success } = await get(id);
      if (data && success) {
        setBusAutomationCode(data._4uBusAutomation.code);
        setBusAutomationId(data._4uBusAutomation.id);
        const hasBatchConfiguration =
          physicalPersonSingleBaseAutomationCodes.includes(
            data._4uBusAutomation.code,
          );
        const newData: TIntegrationRuleForm = {
          ...data,
          uBusAutomation: data._4uBusAutomation,
          iProcesse: data.iProcesse,
          startDate: dateOnlyStringToNewDate(data.startDate),
          endDate: dateOnlyStringToNewDate(data.endDate),
          initialDate: dateOnlyStringToNewDate(data.initialDate),
          batchSize: hasBatchConfiguration
            ? getValidBatchSize(data.batchSize)
            : data.batchSize,
          maxWorkers: hasBatchConfiguration
            ? getValidMaxWorkers(data.maxWorkers)
            : data.maxWorkers,
        };
        return { data: newData, success: true };
      }

      return { data, success };
    },
    [get],
  );

  const redirectOnSuccess = useCallback(
    (data?: any) => {
      if (!id) {
        if (data) {
          setMessage({
            message: t(translationPath + "created_successfully"),
            type: "success",
          });
          navigate(
            `/e/${eI}/4uBus/automations/integrationRule/${data.id}/edit`,
            {
              replace: true,
            },
          );
        }
      } else {
        navigate(`/e/${eI}/4uBus/automations/integrationRule/`);
      }
    },
    [navigate, setMessage, t, id, eI],
  );

  const loading = sLoading || uLoading || gLoading;

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        label: t(translationPath + "i_services"),
        icon: <MiscellaneousServicesRoundedIcon fontSize="small" />,
        component: (
          <IntegrationRuleIServicePage
            busAutomationCode={busAutomationCode}
            busAutomationId={busAutomationId}
          />
        ),
      },
    ];

    if (
      busAutomationCode === 2 ||
      busAutomationCode === 6 ||
      busAutomationCode === 9 ||
      busAutomationCode === 14 ||
      busAutomationCode === 15 ||
      busAutomationCode === 16 ||
      busAutomationCode === 17 ||
      busAutomationCode === 19
    ) {
      baseTabs.push({
        label: t(translationPath + "integration_rule_relationship_group"),
        icon: <WorkspacesRoundedIcon fontSize="small" />,
        component: (
          <IntegrationRuleRelationshipGroupPage
            busAutomationCode={busAutomationCode}
          />
        ),
      });
    }
    if (
      busAutomationCode === 5 ||
      busAutomationCode === 11 ||
      busAutomationCode === 19 ||
      busAutomationCode === 21
    ) {
      baseTabs.push({
        label: t(translationPath + "transfers"),
        icon: <SwapHorizIcon fontSize="small" />,
        component: <IntegrationRuleMovPage busAutomationCode={busAutomationCode} />,
      });
    }
    if (
      busAutomationCode === 6 ||
      busAutomationCode === 9 ||
      busAutomationCode === 10
    ) {
      baseTabs.push({
        label: t(translationPath + "parameters"),
        icon: <SettingsIcon fontSize="small" />,
        component: (
          <IntegrationRuleParamPage busAutomationCode={busAutomationCode} />
        ),
      });
    }
    if (busAutomationCode === 18) {
      baseTabs.push({
        label: "Notificações",
        icon: <NotificationsIcon fontSize="small" />,
        component: <IntegrationRuleNotificationsPage />,
      });
    }

    if (
      busAutomationCode &&
      SYNC_PHYSICAL_PERSON_SINGLE_BASE_UPDATE_AUTOMATION_CODES.includes(
        busAutomationCode,
      )
    ) {
      baseTabs.push({
        label: t(translationPath + "rules"),
        icon: <RuleIcon fontSize="small" />,
        component: <IntegrationRuleFieldPage />,
      });
    }

    return baseTabs;
  }, [t, busAutomationCode, busAutomationId]);

  return (
    <>
      <FormContainer<TIntegrationRuleForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "integration_rule")}
        schema={integrationRuleSchema}
        subtitle={t(translationPath + "new_integration_rule")}
        subtitleWatchField="name"
        redirectOnSave={false}
        onSuccess={redirectOnSuccess}
        formActionsOnBottom={false}
        defaultValues={{
          batchSize: 50,
          maxWorkers: 5,
          onlyRequestableLabExams: false,
        }}
        marginBottom={5}
        customHeight="fit-content"
        fields={() => <BasicData disableAutomationEdit />}
      />

      {id && (
        <Grid container spacing={2} sx={{ width: "80%", margin: "0 auto" }}>
          <Grid item sm={12} mb={5}>
            <FormTabs tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default IntegrationRule;
